import { EngineFailure } from '../../core/errors';
import type { EngineErrorCode } from '../../core/types';

/**
 * 前端侧记账与 AI 调用封装。
 *
 * 约束（与 docs/05、docs/10 一致）：
 * - 前端**永远不自己算价格、不自己改余额**；单价与余额都来自服务端返回值；
 * - 本地转换的 `start` 只上报元数据（格式/大小/引擎/幂等键），不传文件内容；
 * - AI 通道才会上传页面图片，且必须先拿到已记账的 `jobId`。
 */

/** 服务端错误码 → 引擎错误码：让任务台能复用同一套文案与建议。 */
const CODE_MAP: Record<string, EngineErrorCode> = {
  LOGIN_REQUIRED: 'LOGIN_REQUIRED',
  FREE_QUOTA_EXHAUSTED: 'FREE_QUOTA_EXHAUSTED',
  CREDITS_INSUFFICIENT: 'CREDITS_INSUFFICIENT',
  CREDITS_POLICY_MISSING: 'BILLING_UNAVAILABLE',
  CREDITS_UNKNOWN_OPERATION: 'BILLING_UNAVAILABLE',
  CREDITS_SCOPE_REQUIRED: 'BILLING_UNAVAILABLE',
  CREDITS_UNAVAILABLE: 'BILLING_UNAVAILABLE',
  UPLOAD_TOO_LARGE: 'UPLOAD_TOO_LARGE',
  AI_UNAVAILABLE: 'AI_UNAVAILABLE',
  AI_UPSTREAM_FAILED: 'AI_UPSTREAM_FAILED',
  RATE_LIMIT_LOCAL: 'RATE_LIMITED',
  MAINTENANCE: 'BILLING_UNAVAILABLE',
  UNAUTHORIZED: 'LOGIN_REQUIRED',
  CONFLICT: 'INTERNAL_ERROR',
};

interface ApiFailure {
  ok: false;
  code?: string;
  message?: string;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw new EngineFailure('BILLING_UNAVAILABLE', `无法连接服务端：${error instanceof Error ? error.message : String(error)}`);
  }

  const payload = (await response.json().catch(() => null)) as
    | { ok?: boolean; data?: T; code?: string; message?: string }
    | null;
  if (!payload || payload.ok !== true) {
    const code = CODE_MAP[payload?.code ?? ''] ?? 'INTERNAL_ERROR';
    throw new EngineFailure(code, payload?.message ?? `服务端返回 ${response.status}`);
  }
  return payload.data as T;
}

/** 12 字节随机数的十六进制串（幂等键的随机段）。 */
function randomHex12(): string {
  const bytes = new Uint8Array(12);
  // 一律用 Web Crypto 取随机：`crypto.randomUUID` 缺失时退回 `Math.random` 会让并发下更容易撞键，
  // 而幂等键一旦重复，服务端会把两次不同的转换当成同一次重放（第二次直接被吞掉）。
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  let hex = '';
  for (const byte of bytes) hex += byte.toString(16).padStart(2, '0');
  return hex;
}

/** 幂等键：一次「发起」一个键；重试必须换新键（旧任务若已结束会被服务端拒绝重放）。 */
export function newIdempotencyKey(): string {
  return `conv-${Date.now().toString(36)}-${randomHex12()}`;
}

export interface StartInput {
  mode: 'local' | 'ai';
  sourceFormat: string;
  targetFormat: string;
  sourceSize: number;
  engineId: string;
  idempotencyKey: string;
}

export interface StartResult {
  jobId: string;
  mode: 'local' | 'ai';
  operation: string;
  cost: number;
  charged: boolean;
  replayed: boolean;
  status?: string;
  balanceAfter?: number | null;
  freeRemaining?: number;
}

export function startConversion(input: StartInput): Promise<StartResult> {
  return postJson<StartResult>('/api/conversions/start', input);
}

export interface FinishInput {
  status: 'succeeded' | 'failed';
  errorCode?: string;
  outputSize?: number;
}

export interface FinishResult {
  jobId: string;
  status: string;
  refunded: boolean;
  replayed?: boolean;
}

export function finishConversion(jobId: string, input: FinishInput): Promise<FinishResult> {
  return postJson<FinishResult>(`/api/conversions/${encodeURIComponent(jobId)}/finish`, input);
}

export interface AiExtractResult {
  jobId: string;
  text: string;
  provider: string;
  model: string;
  pages: number;
  attempts: number;
}

export function extractPages(input: {
  jobId: string;
  targetFormat: 'txt' | 'md';
  pages: string[];
}): Promise<AiExtractResult> {
  return postJson<AiExtractResult>('/api/ai/extract', input);
}

export interface UsagePayload {
  authenticated: boolean;
  day: string;
  pricing: { localCost: number; aiCost: number; currency: 'credit' };
  anonymous: { limit: number; used: number; remaining: number };
  credits: { balance: number; dailyPoints: number; bonusPoints: number; dailyGrantQuota: number } | null;
  creditsError: string | null;
  aiAvailable: boolean;
}

/** 用量与单价：未登录也可调用（界面需要展示「今日还剩几次」）。 */
export async function fetchUsage(): Promise<UsagePayload | null> {
  try {
    const response = await fetch('/api/account/usage', { headers: { accept: 'application/json' } });
    if (!response.ok) return null;
    const payload = (await response.json()) as { ok?: boolean; data?: UsagePayload };
    return payload.ok === true && payload.data ? payload.data : null;
  } catch {
    return null;
  }
}
